import { forwardRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { KeystoneMark } from '@/components/g5';

/**
 * "Aura" action kit — pill buttons for the public site. Soft, product-grade:
 * the primary glows gently on hover, the soft variant deepens its tint.
 * Reads `--g5-*` color tokens; render inside `.ashlar-public`.
 */

type Size = 'sm' | 'md';

const sizes: Record<Size, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
};

interface AuraButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: Size;
  /** `primary` solid violet · `soft` violet tint · `inverse`/`ghost-inverse` for accent panels. */
  variant?: 'primary' | 'soft' | 'inverse' | 'ghost-inverse';
  children: React.ReactNode;
}

export const AuraButton = forwardRef<HTMLButtonElement, AuraButtonProps>(
  ({ size = 'md', variant = 'primary', className, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-full font-semibold',
          sizes[size],
          `aura-btn-${variant}`,
          (variant === 'primary' || variant === 'inverse') && 'aura-sheen',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
        {...props}
      >
        {children}
        {variant === 'primary' && <KeystoneMark size={13} className="-ml-0.5" />}
      </button>
    );
  },
);
AuraButton.displayName = 'AuraButton';

/** Inline text link with a trailing arrow. */
export const ArrowLink = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <button ref={ref} className={cn('aura-link inline-flex items-center gap-1.5 text-sm', className)} {...props}>
        {children}
        <ArrowRight className="h-4 w-4" />
      </button>
    );
  },
);
ArrowLink.displayName = 'ArrowLink';
